import axios, { AxiosError, AxiosRequestConfig } from 'axios';

const CSRF_ROUTE = '/api/csrf';
const DEFAULT_CSRF_HEADER = 'x-csrf-token';
const DEFAULT_CSRF_EXPIRY_SECONDS = 60 * 60; // 1 hour
const CSRF_REFRESH_BUFFER_SECONDS = 60;

type CsrfAxiosRequestConfig = AxiosRequestConfig & { _csrfRetried?: boolean };

let cachedCsrfToken: string | null = null;
let csrfTokenPromise: Promise<string> | null = null;
let csrfRefreshTimer: ReturnType<typeof setTimeout> | null = null;
let interceptorInitialized = false;

const isCsrfClientEnabled = (): boolean =>
  process.env.NEXT_PUBLIC_CSRF_PROTECTION_ENABLED?.toLowerCase() === 'true';

const getCsrfHeaderName = (): string =>
  process.env.NEXT_PUBLIC_CSRF_HEADER_NAME?.trim().toLowerCase() || DEFAULT_CSRF_HEADER;

const isMutatingRequest = (method: AxiosRequestConfig['method']): boolean => {
  const normalizedMethod = (method || '').toLowerCase();
  return (
    normalizedMethod === 'post' ||
    normalizedMethod === 'put' ||
    normalizedMethod === 'patch' ||
    normalizedMethod === 'delete'
  );
};

const isInternalApiRequest = (url?: string): boolean => !!url && url.startsWith('/api/');

const isCsrfForbiddenResponse = (status: number | undefined, data: unknown): boolean => {
  if (status !== 403) return false;
  const errorMessage =
    data && typeof data === 'object' && 'error' in data
      ? (data as { error?: unknown }).error
      : undefined;
  return errorMessage === 'Missing CSRF token' || errorMessage === 'Invalid CSRF token';
};

const clearCachedCsrfToken = (): void => {
  cachedCsrfToken = null;
  csrfTokenPromise = null;
};

const clearCsrfRefreshTimer = (): void => {
  if (!csrfRefreshTimer) return;
  clearTimeout(csrfRefreshTimer);
  csrfRefreshTimer = null;
};

const scheduleCsrfRefresh = (expiresInSeconds?: number): void => {
  clearCsrfRefreshTimer();

  const expirySeconds =
    typeof expiresInSeconds === 'number' && expiresInSeconds > 0
      ? expiresInSeconds
      : DEFAULT_CSRF_EXPIRY_SECONDS;
  const refreshInSeconds = Math.max(expirySeconds - CSRF_REFRESH_BUFFER_SECONDS, 1);

  csrfRefreshTimer = setTimeout(() => {
    clearCachedCsrfToken();
    getCsrfToken().catch(() => {
      // Request interceptor will attempt again on next mutating API call.
    });
  }, refreshInSeconds * 1000);
};

const fetchCsrfToken = async (): Promise<string> => {
  const response = await axios.get(CSRF_ROUTE, {
    params: { _ts: Date.now() },
    headers: {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    },
  });
  const token = response?.data?.csrfToken;
  const tokenExpiresInSeconds = response?.data?.csrfTokenExpiresInSeconds;

  if (!token || typeof token !== 'string') {
    throw new Error('Unable to fetch CSRF token');
  }

  cachedCsrfToken = token;
  scheduleCsrfRefresh(tokenExpiresInSeconds);
  return token;
};

const getCsrfToken = async (): Promise<string> => {
  if (cachedCsrfToken) {
    return cachedCsrfToken;
  }

  if (!csrfTokenPromise) {
    csrfTokenPromise = fetchCsrfToken().finally(() => {
      csrfTokenPromise = null;
    });
  }

  return csrfTokenPromise;
};

const applyCsrfHeader = async (config: AxiosRequestConfig): Promise<void> => {
  const csrfToken = await getCsrfToken();
  const csrfHeaderName = getCsrfHeaderName();

  config.headers = {
    ...(config.headers || {}),
    [csrfHeaderName]: csrfToken,
  };
};

export const initializeAxiosCsrfProtection = (): void => {
  if (!isCsrfClientEnabled()) return;
  if (typeof window === 'undefined') return;
  if (interceptorInitialized) return;

  axios.interceptors.request.use(async (config) => {
    if (!isInternalApiRequest(config.url) || !isMutatingRequest(config.method)) {
      return config;
    }

    await applyCsrfHeader(config);
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const config = error.config as CsrfAxiosRequestConfig | undefined;
      if (!config || config._csrfRetried) {
        return Promise.reject(error);
      }

      if (
        !isCsrfForbiddenResponse(error.response?.status, error.response?.data) ||
        !isInternalApiRequest(config.url) ||
        !isMutatingRequest(config.method)
      ) {
        return Promise.reject(error);
      }

      config._csrfRetried = true;
      clearCachedCsrfToken();

      try {
        await applyCsrfHeader(config);
        return axios.request(config);
      } catch (retryError) {
        return Promise.reject(retryError);
      }
    }
  );

  interceptorInitialized = true;
};
